import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Sparkles, Copy, LogOut } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export const AICaptionPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [caption, setCaption] = useState('');
  
  // Property details form
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [sqft, setSqft] = useState('');
  const [description, setDescription] = useState('');
  
  // URL form
  const [propertyUrl, setPropertyUrl] = useState('');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const generateFromDetails = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post('/ai/generate-caption', {
        property_details: {
          beds,
          baths,
          sqft,
          description
        }
      });
      setCaption(response.data.caption);
      toast.success('Caption generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate caption');
    } finally {
      setLoading(false);
    }
  };

  const generateFromUrl = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiClient.post('/ai/generate-caption', {
        property_url: propertyUrl
      });
      setCaption(response.data.caption);
      toast.success('Caption generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate caption');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(caption);
    toast.success('Caption copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glassmorphic">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="ai-caption-logo">RealProp</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">Welcome, {user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b border-border/50">
        <div className="container mx-auto px-6">
          <div className="flex gap-6 py-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground" data-testid="nav-dashboard">Dashboard</Link>
            <Link to="/templates" className="text-muted-foreground hover:text-foreground" data-testid="nav-templates">Templates</Link>
            <Link to="/campaigns" className="text-muted-foreground hover:text-foreground" data-testid="nav-campaigns">My Campaigns</Link>
            <Link to="/magic-create" className="text-muted-foreground hover:text-foreground" data-testid="nav-magic-create">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent font-medium">✨ RealProp Magic</span>
            </Link>
            <Link to="/print-center" className="text-muted-foreground hover:text-foreground" data-testid="nav-print-center">Print Center</Link>
            <Link to="/email-lists" className="text-muted-foreground hover:text-foreground" data-testid="nav-email-lists">Email Lists</Link>
            {user?.role === 'admin' && (
              <Link to="/design-studio" className="text-muted-foreground hover:text-foreground" data-testid="nav-admin">
                <span className="text-orange-500 font-medium">⚙️ Design Studio</span>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* AI Caption Content */}
      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="ai-caption-title">AI Caption Generator</h2>
          </div>
          <p className="text-muted-foreground">Generate engaging social media captions powered by AI</p>
        </div>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details" data-testid="tab-details">Property Details</TabsTrigger>
            <TabsTrigger value="url" data-testid="tab-url">Property URL</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-6">
            <div className="p-6 border border-border/50 bg-card rounded-lg">
              <form onSubmit={generateFromDetails} className="space-y-4" data-testid="details-form">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="beds">Bedrooms</Label>
                    <Input
                      id="beds"
                      type="number"
                      value={beds}
                      onChange={(e) => setBeds(e.target.value)}
                      placeholder="3"
                      className="h-12"
                      data-testid="input-beds"
                    />
                  </div>
                  <div>
                    <Label htmlFor="baths">Bathrooms</Label>
                    <Input
                      id="baths"
                      type="number"
                      value={baths}
                      onChange={(e) => setBaths(e.target.value)}
                      placeholder="2"
                      className="h-12"
                      data-testid="input-baths"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sqft">Square Feet</Label>
                    <Input
                      id="sqft"
                      type="number"
                      value={sqft}
                      onChange={(e) => setSqft(e.target.value)}
                      placeholder="2000"
                      className="h-12"
                      data-testid="input-sqft"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Property Description</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Beautiful home in a quiet neighborhood..."
                    rows={4}
                    data-testid="input-description"
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full h-12" data-testid="generate-details-button">
                  {loading ? 'Generating...' : 'Generate Caption'}
                </Button>
              </form>
            </div>
          </TabsContent>

          <TabsContent value="url" className="mt-6">
            <div className="p-6 border border-border/50 bg-card rounded-lg">
              <form onSubmit={generateFromUrl} className="space-y-4" data-testid="url-form">
                <div>
                  <Label htmlFor="propertyUrl">Property URL (Zillow, etc.)</Label>
                  <Input
                    id="propertyUrl"
                    type="url"
                    value={propertyUrl}
                    onChange={(e) => setPropertyUrl(e.target.value)}
                    placeholder="https://www.zillow.com/homedetails/..."
                    className="h-12"
                    required
                    data-testid="input-url"
                  />
                </div>

                <Button type="submit" disabled={loading} className="w-full h-12" data-testid="generate-url-button">
                  {loading ? 'Generating...' : 'Generate Caption'}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>

        {/* Generated Caption */}
        {caption && (
          <div className="mt-8 p-6 border border-border/50 bg-card rounded-lg" data-testid="generated-caption">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Generated Caption</h3>
              <Button variant="outline" size="sm" onClick={copyToClipboard} data-testid="copy-caption-button">
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
            <div className="bg-secondary/20 p-4 rounded-md">
              <p className="whitespace-pre-wrap">{caption}</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};